import { useCallback, useEffect, useState } from 'react';

import { preferredTab } from './grouping';
import type { RoadmapItem } from './types';
import type { UpdatesTab } from './UpdatesModal';

/**
 * Which tab the window shows, resolved against data that arrives late.
 *
 * The click and the fetch race, and the click usually wins — so resolving
 * only when the window opens picks a tab against an empty list, lands on
 * What's Coming, and never reconsiders. The user then reads "Nothing queued
 * up right now" while What's New is full.
 *
 * So: resolve on open, resolve again the first time real data lands, then
 * stop. A later poll must not move the tab under someone who is reading, and
 * neither must anything they clicked.
 */
export function useResolvedTab(
	open: boolean,
	items: RoadmapItem[],
	requested: UpdatesTab | undefined,
) {
	const [tab, setTabState] = useState<UpdatesTab>(() =>
		preferredTab(items, requested),
	);
	// Settled once the tab was chosen against real data, or by the user.
	const [settled, setSettled] = useState(false);

	// A fresh open starts over: last session's choice should not persist.
	useEffect(() => {
		if (!open) return;
		setTabState(preferredTab(items, requested));
		setSettled(items.length > 0);
		// `items` is intentionally absent — this is the open transition. The
		// effect below handles data landing afterwards.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, requested]);

	// The late-data pass: exactly once, and only while nothing has settled.
	useEffect(() => {
		if (!open || settled || items.length === 0) return;
		setTabState(preferredTab(items, requested));
		setSettled(true);
	}, [open, settled, items, requested]);

	const setTab = useCallback((next: UpdatesTab) => {
		setTabState(next);
		setSettled(true);
	}, []);

	return { tab, setTab };
}
